import { SearchIcon } from "@heroicons/react/solid";
import { HttpError, useOne } from "@refinedev/core";
import Button from "components/button";
import Input from "components/input";
import Select from "components/select";
import { IEventIdsItem } from "interfaces";
import moment from "moment";
import { useState } from "react";
import Datepicker from "react-tailwindcss-datepicker";
import { twMerge } from "tailwind-merge";
import {
  BOOLEAN_STATUS,
  ORDER_IS_VAT,
  ORDER_PAYMENT_METHODS,
  CONTENT_ORDER_PAYMENT_METHODS,
  ORDER_STATUS,
  ORDER_TYPES,
  PAYMENT_METHOD_FREE,
  TICKET_PLATFORMS,
} from "utills/constants";

const ALL_OPTION = { label: "All", value: "" };

export const Filter = ({
  currentFilterValues,
  setFilters,
}: {
  currentFilterValues: any;
  setFilters: any;
}) => {
  const { data: eventsData, isLoading } = useOne<IEventIdsItem[], HttpError>({
    resource: "content/dropdown/events?uncheck=true",
    id: "",
    meta: {
      resource: "data",
    },
  });

  const eventIds = (
    (eventsData?.data ?? []).length > 0 ? [ALL_OPTION] : []
  ).concat(
    (eventsData?.data ?? []).map((event: IEventIdsItem) => ({
      value: event.id,
      label: event.name_vi,
    }))
  );
  const orderTypes = [ALL_OPTION].concat(ORDER_TYPES ?? []);
  const paymentMethods = [ALL_OPTION]
    .concat(CONTENT_ORDER_PAYMENT_METHODS ?? [])
    .concat(PAYMENT_METHOD_FREE);
  const orderStatusList = [ALL_OPTION].concat(ORDER_STATUS ?? []);
  const isVATList = [ALL_OPTION].concat(ORDER_IS_VAT ?? []);
  const ticketPlatformList = [ALL_OPTION].concat(TICKET_PLATFORMS ?? []);

  const _handleSubmitFilter = () => {
    setFilters([
      { field: "q", operator: "contains", value: keyword },
      { field: "event_id", operator: "eq", value: eventId },
      { field: "order_type", operator: "eq", value: orderType },
      { field: "payment_method", operator: "eq", value: paymentMethod },
      { field: "order_status", operator: "eq", value: orderStatus },
      { field: "is_vat", operator: "eq", value: isVAT },
      { field: "ticket_platform", operator: "eq", value: ticketPlatform },
      { field: "is_private_flow", operator: "eq", value: isPrivateFlow },
      {
        field: "gte",
        operator: "eq",
        value: createdDate.startDate
          ? moment(createdDate.startDate).format("YYYY-MM-DD")
          : "",
      },
      {
        field: "lte",
        operator: "eq",
        value: createdDate.endDate
          ? moment(createdDate.endDate).format("YYYY-MM-DD")
          : "",
      },
      {
        field: "sc_from",
        operator: "eq",
        value: paymentDate.startDate
          ? moment(paymentDate.startDate).format("YYYY-MM-DD")
          : "",
      },
      {
        field: "sc_to",
        operator: "eq",
        value: paymentDate.endDate
          ? moment(paymentDate.endDate).format("YYYY-MM-DD")
          : "",
      },
      { field: "reference_number", operator: "eq", value: referenceNumber },
      { field: "page", operator: "eq", value: 1 },
    ]);
  };

  const [keyword, setKeyword] = useState(currentFilterValues.q);
  const [eventId, setEventId] = useState(currentFilterValues.event_id);
  const [orderType, setOrderType] = useState(currentFilterValues.order_type);
  const [paymentMethod, setPaymentMethod] = useState(
    currentFilterValues.payment_method
  );
  const [orderStatus, setOrderStatus] = useState(
    currentFilterValues.order_status
  );
  const [isVAT, setIsVAT] = useState(currentFilterValues.is_vat);
  const [createdDate, setCreatedDate] = useState({
    startDate: currentFilterValues.gte || "",
    endDate: currentFilterValues.lte || "",
  });
  const [paymentDate, setPaymentDate] = useState({
    startDate: currentFilterValues.sc_from || "",
    endDate: currentFilterValues.sc_to || "",
  });

  const [ticketPlatform, setTicketPlatform] = useState(
    currentFilterValues.ticket_platform
  );
  const [isPrivateFlow, setIsPrivateFlow] = useState(
    currentFilterValues.is_private_flow
  );

  const [referenceNumber, setReferenceNumber] = useState(
    currentFilterValues.reference_number
  );

  return (
    <div className="w-full flex flex-col gap-y-4">
      <div className="flex flex-col gap-y-4 md:flex-row md:items-end md:gap-x-3">
        <div className="flex-1">
          <label htmlFor="name" className="block text-sm font-medium leading-6">
            Search
          </label>
          <div className="mt-2">
            <Input
              className="px-2 py-2"
              label="Search"
              id="name"
              type="text"
              placeholder="Search by Order code, Buyer Phone/Email, Coupon, Private passcode/email"
              value={keyword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setKeyword(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  _handleSubmitFilter();
                }
              }}
            />
          </div>
        </div>
        <div className="flex-1">
          <label htmlFor="name" className="block text-sm font-medium leading-6">
            Search transaction code
          </label>
          <div className="mt-2">
            <Input
              className="px-2 py-2"
              id="name"
              type="text"
              placeholder="Search by Transaction code"
              value={referenceNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setReferenceNumber(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  _handleSubmitFilter();
                }
              }}
            />
          </div>
        </div>
        <div className="flex-1">
          <label
            htmlFor="event_id"
            className="block text-sm font-medium leading-6"
          >
            Event
          </label>
          <div className="mt-2">
            <Select
              options={eventIds}
              isLoading={isLoading}
              id="event_id"
              placeholder="Select Event"
              className="py-0.5"
              value={eventIds.find((d) => d.value === eventId)}
              onChange={(selected: { label: string; value: string }) =>
                setEventId(selected?.value)
              }
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-y-4 md:flex-row md:items-end md:gap-x-3">
        <div className="flex-1">
          <label
            htmlFor="order_type"
            className="block text-sm font-medium leading-6"
          >
            Types
          </label>
          <div className="mt-2">
            <Select
              options={orderTypes}
              id="order_type"
              placeholder="Select Type"
              className="py-0.5"
              value={orderTypes.find((d) => d.value === orderType)}
              onChange={(selected: { label: string; value: string }) =>
                setOrderType(selected?.value)
              }
            />
          </div>
        </div>
        <div className="flex-1">
          <label
            htmlFor="payment_method"
            className="block text-sm font-medium leading-6"
          >
            Payment method
          </label>
          <div className="mt-2">
            <Select
              options={paymentMethods}
              id="payment_method"
              placeholder="Select Payment Method"
              className="py-0.5"
              value={paymentMethods.find((d) => d.value === paymentMethod)}
              onChange={(selected: { label: string; value: string }) =>
                setPaymentMethod(selected?.value)
              }
            />
          </div>
        </div>
        <div className="flex-1">
          <label
            htmlFor="order_status"
            className="block text-sm font-medium leading-6"
          >
            Status
          </label>
          <div className="mt-2">
            <Select
              options={orderStatusList}
              id="order_status"
              placeholder="Select Status"
              className="py-0.5"
              value={orderStatusList.find((d) => d.value === orderStatus)}
              onChange={(selected: { label: string; value: string }) =>
                setOrderStatus(selected?.value)
              }
            />
          </div>
        </div>
        <div className="flex-1">
          <label
            htmlFor="is_vat"
            className="block text-sm font-medium leading-6"
          >
            VAT
          </label>
          <div className="mt-2">
            <Select
              options={isVATList}
              id="is_vat"
              placeholder="Select Is VAT"
              className="py-0.5"
              value={isVATList.find((d) => d.value === isVAT)}
              onChange={(selected: { label: string; value: string }) =>
                setIsVAT(selected?.value)
              }
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-y-4 md:flex-row md:items-end md:gap-x-3">
        <div className="flex-1">
          <label
            htmlFor="created_date"
            className="block text-sm font-medium leading-6"
          >
            Created Date
          </label>
          <div className="mt-2">
            <Datepicker
              key={"created_date"}
              value={createdDate}
              onChange={(value) =>
                setCreatedDate({
                  startDate: value?.startDate
                    ? moment(value?.startDate).format("YYYY-MM-DD")
                    : null,
                  endDate: value?.endDate
                    ? moment(value?.endDate).format("YYYY-MM-DD")
                    : null,
                })
              }
              displayFormat="DD/MM/YYYY"
              placeholder="Start date - End date"
              inputClassName="date-picker"
              popoverDirection="down"
              containerClassName={(className) => twMerge(className, "date-picker-container")}
            />
          </div>
        </div>
        <div className="flex-1">
          <label
            htmlFor="created_date"
            className="block text-sm font-medium leading-6"
          >
            Payment date
          </label>
          <div className="mt-2">
            <Datepicker
              value={paymentDate}
              onChange={(value) =>
                setPaymentDate({
                  startDate: value?.startDate
                    ? moment(value?.startDate).format("YYYY-MM-DD")
                    : null,
                  endDate: value?.endDate
                    ? moment(value?.endDate).format("YYYY-MM-DD")
                    : null,
                })
              }
              displayFormat="DD/MM/YYYY"
              placeholder="Start date - End date"
              inputClassName="date-picker"
              popoverDirection="down"
              containerClassName={(className) => twMerge(className, "date-picker-container")}
            />
          </div>
        </div>
        <div className="flex-1">
          <label
            htmlFor="is_private_flow"
            className="block text-sm font-medium leading-6"
          >
            Private flow
          </label>
          <div className="mt-2">
            <Select
              options={BOOLEAN_STATUS}
              id="is_private_flow"
              placeholder="Select Is Private flow"
              isClearable={true}
              className="py-0.5"
              value={BOOLEAN_STATUS.find((d) => d.value === isPrivateFlow)}
              onChange={(selected: { label: string; value: string }) =>
                setIsPrivateFlow(selected?.value)
              }
            />
          </div>
        </div>
        <div className="flex-1">
          <label
            htmlFor="ticket_platform"
            className="block text-sm font-medium leading-6"
          >
            Ticket platform
          </label>
          <div className="mt-2">
            <Select
              options={ticketPlatformList}
              id="ticket_platform"
              placeholder="Select Ticket Platform"
              className="py-0.5"
              value={ticketPlatformList.find((d) => d.value === ticketPlatform)}
              onChange={(selected: { label: string; value: string }) =>
                setTicketPlatform(selected?.value)
              }
            />
          </div>
        </div>
        <div className="shrink-0">
          <label
            htmlFor="status"
            className="block text-sm font-medium leading-6"
          >
            <span className="sr-only">Search</span>
          </label>
          <div className="mt-2 w-full">
            <Button
              type="submit"
              className="w-full bg-white rounded-md px-3 text-brand-500 py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85"
              onClick={_handleSubmitFilter}
            >
              <SearchIcon className="w-3 h-4 text-brand-500 mr-2" /> Search
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
